import React from 'react';
import { Track, Feedback } from '../../types/artist';
import './TrackCard.css';

export interface TrackCardProps {
  track: Track;
  isSelected?: boolean;
  isPlaying?: boolean;
  onClick?: (track: Track) => void;
  onPlayPause?: (track: Track) => void;
  
  // Display options
  showArtist?: boolean;
  showGenre?: boolean;
  showDate?: boolean;
  showUploadDate?: boolean;
  showReviewDate?: boolean;
  showFeedbackCount?: boolean;
  showFeedbackSummary?: boolean;
  showAIBadge?: boolean;
  
  // Action options
  showPlayButton?: boolean;
  showShareButton?: boolean;
  showDeleteButton?: boolean;
  showReviewStatus?: boolean;
  
  // Callbacks for actions
  onShare?: (track: Track, e: React.MouseEvent) => void;
  onDelete?: (track: Track, e: React.MouseEvent) => void;
  onSetToReview?: (track: Track, e: React.MouseEvent) => void;
  onArtistClick?: (artistId?: number) => void;
  
  // Custom data for different contexts
  reviewDate?: string; // For MyReviews component
  trackIndex?: number; // For indexed selection
  customPlayClick?: (track: Track, index?: number, autoPlay?: boolean) => void;
  
  // Styling
  className?: string;
  variant?: 'default' | 'info-only'; // info-only for RequestFeedback display
}

export const TrackCard: React.FC<TrackCardProps> = ({
  track,
  isSelected = false,
  isPlaying = false,
  onClick,
  onPlayPause,
  
  // Display options with sensible defaults
  showArtist = true,
  showGenre = true,
  showDate = true,
  showUploadDate = true,
  showReviewDate = false,
  showFeedbackCount = true,
  showFeedbackSummary = false,
  showAIBadge = true,
  
  // Action options
  showPlayButton = true,
  showShareButton = false,
  showDeleteButton = false,
  showReviewStatus = false,
  
  // Callbacks
  onShare,
  onDelete,
  onSetToReview,
  onArtistClick,
  
  // Custom data
  reviewDate,
  trackIndex,
  customPlayClick,
  
  // Styling
  className = '',
  variant = 'default'
}) => {
  
  const getAverageRating = (feedback: Feedback[]): number => {
    if (feedback.length === 0) return 0;
    const sum = feedback.reduce((acc, f) => acc + f.overallRating, 0);
    return sum / feedback.length;
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(track);
    }
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (customPlayClick && trackIndex !== undefined) {
      // For MyReviews with auto-play and index
      if (isSelected) {
        // Same track - toggle play/pause
        if (onPlayPause) {
          onPlayPause(track);
        }
      } else {
        // Different track - select and auto-play
        customPlayClick(track, trackIndex, true);
      }
    } else {
      // Standard behavior for other components
      if (!isSelected && onClick) {
        onClick(track);
      }
      if (onPlayPause) {
        setTimeout(() => onPlayPause(track), 100);
      }
    }
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onShare) {
      onShare(track, e);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(track, e);
    }
  };

  const handleSetToReviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSetToReview) {
      onSetToReview(track, e);
    }
  };

  const handleArtistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onArtistClick && track.artist?.id) {
      onArtistClick(track.artist.id);
    }
  };

  const cardClassName = `track-card ${isSelected ? 'selected' : ''} ${className}`;
  const isInteractive = variant !== 'info-only';

  return (
    <div 
      className={cardClassName}
      onClick={isInteractive ? handleCardClick : undefined}
      style={{ cursor: isInteractive ? 'pointer' : 'default' }}
    >
      <div className="track-header">
        <div className="track-title-section">
          <div className="track-title-row">
            <h4 className="track-title">{track.title}</h4>
            {showAIBadge && track.artist?.usesAI && (
              <span className="ai-badge" title="This artist uses AI in their creative process">
                AI
              </span>
            )}
          </div>
          <div className="track-info">
            {showArtist && track.artist && (
              <span 
                className={`track-artist ${onArtistClick ? 'clickable' : ''}`}
                onClick={onArtistClick ? handleArtistClick : undefined}
                title={onArtistClick ? 'View artist profile' : undefined}
              >
                by {track.artist.name}
              </span>
            )}
            {showGenre && track.genre && (
              <p className="track-genre">{track.genre}</p>
            )}
            {showDate && showUploadDate && (
              <p className="track-date">
                Uploaded: {new Date(track.uploadDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        
        {/* Header Actions (Share/Delete for TrackManager) */}
        {(showShareButton || showDeleteButton) && (
          <div className="track-header-actions">
            {showShareButton && (
              <button 
                className="share-button"
                onClick={handleShareClick}
                title="Share track for feedback"
                disabled={!track.publicId}
              >
                📤
              </button>
            )}
            {showDeleteButton && (
              <button 
                className="delete-button-header"
                onClick={handleDeleteClick}
                title="Delete track"
              >
                🗑️
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* Review Status Section (TrackManager) */}
      {showReviewStatus && (
        <div className="track-status-section">
          {track.underReview ? (
            <span className="track-status under-review">
              Under Review
            </span>
          ) : (
            <button 
              className="track-status clickable"
              onClick={handleSetToReviewClick}
              title="Click to set track for review"
            >
              Set to Review
            </button>
          )}
        </div>
      )}

      {/* Review Date (MyReviews) */}
      {showReviewDate && reviewDate && (
        <div className="track-review-date">
          Reviewed: {reviewDate}
        </div>
      )}

      {/* Feedback Count (Simple) */}
      {showFeedbackCount && track.feedback && track.feedback.length > 0 && !showFeedbackSummary && (
        <div className="track-feedback-count">
          <span>{track.feedback.length} review{track.feedback.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Feedback Summary (Detailed - TrackManager) */}
      {showFeedbackSummary && track.feedback && track.feedback.length > 0 && (
        <div className="track-feedback-summary">
          <div className="feedback-stats">
            <span className="feedback-count">
              {track.feedback.length} review{track.feedback.length !== 1 ? 's' : ''}
            </span>
            <span className="average-rating">
              ⭐ {getAverageRating(track.feedback).toFixed(1)}
            </span>
          </div>
        </div>
      )}

      {/* Play Actions */}
      {showPlayButton && (track.audioUrl || track.id) ? (
        <div className="track-actions">
          <button 
            className="play-button"
            onClick={handlePlayClick}
          >
            {isSelected && isPlaying ? '⏸️' : '▶️'}
          </button>
        </div>
      ) : showPlayButton ? (
        <div className="track-actions">
          <span className="audio-placeholder">🎵 Processing audio...</span>
        </div>
      ) : null}
    </div>
  );
};